import { type LinksFunction, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from '@remix-run/react';
import { ThemeProvider } from 'flowbite-react';
import { useChangeLanguage } from 'remix-i18next/react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { GenericErrorBoundary } from '#common/components/ErrorBoundary.tsx';
import { validateEditMode } from '#common/i18n/edit-mode.server.ts';
import { EditModeProvider } from '#common/i18n/edit-mode.tsx';
import { localeCookie } from '#common/i18n/i18n.server';
import { crispyvoiceTheme } from '#common/theme.ts';
import { ConsentHandling } from '#common/utils/consent-handling.js';
import { serializeCookieByConsent } from '#common/utils/consent.server.js';
import { honeypot } from '#common/utils/honeypot.server';
import { DefaultLangProvider, getUrlWithoutLang } from '#common/utils/i18n.js';
import { SessionId } from '#common/utils/session-id.tsx';
import { PlausibleTracker } from '#common/utils/tracking/plausible.tsx';
import { getLocale, getMetaTitle } from '#speaker-shop/i18n/i18n.server';
import { getUser } from '#speaker-shop/session.server';
import '#speaker-shop/styles/app.css';
import { defaultLang } from './i18n/config';
import { MediumsIconLoader } from './utils/mediums-icon-loader';
import { environment, PublicEnv } from './utils/public-env';
import { useFirstPartyGtm } from '#common/utils/tracking/google-analytics.tsx';
import { AffiliateRefProvider } from '#common/utils/tracking/affiliate-ref-context.tsx';
import { setTtclidCookieIfNeeded } from '#common/utils/tracking/tiktok.server.ts';
import { setFbcCookieIfNeeded } from '#common/utils/tracking/facebook.server.ts';
import { setGoogleClickIdCookiesIfNeeded } from '#common/utils/tracking/google-ads.server.ts';
import { setAffiliateRefCookieIfNeeded } from '#common/utils/tracking/affiliate.server.ts';
import { resolveVoiceAndLangParams } from './utils/resolve-voice';
import { resolveShopContext } from './utils/shop-context.server';

export const handle = {
    i18n: [
        'common',
        'footer',
        'navigation',
        'contact',
        'core-shop',
        'hero',
        'medium-selection',
        'audio-generation',
        'checkout',
    ]
};

export async function loader(args: LoaderFunctionArgs) {
    const { request, params } = args;
    const { voiceSlug, lang } = resolveVoiceAndLangParams(params);
    const shopContext = await resolveShopContext(request.url, voiceSlug);

    const user = await getUser(request);
    const locale = await getLocale(user, lang, request);
    const honeyProps = await honeypot.getInputProps();
    const env = environment();

    const headers = new Headers();
    headers.append(
        'Set-Cookie',
        await serializeCookieByConsent(request, 'functionality', localeCookie, locale)
    );

    // Capture affiliate attribution (`?ref=`) on every shop page, in all environments.
    await setAffiliateRefCookieIfNeeded(request, headers);

    if (process.env.ENV === 'prod') {
        await setTtclidCookieIfNeeded(request, headers);
        await setFbcCookieIfNeeded(request, headers);
        await setGoogleClickIdCookiesIfNeeded(request, headers);
    }

    return typedjson({
        unifiedShopPage: shopContext.unifiedShopPage,
        shopMode: shopContext.shopMode,
        voiceSlug: shopContext.voiceSlug,
        speakerSlug: shopContext.speakerSlug,
        speakerDisplayName: shopContext.speakerDisplayName,
        locale,
        title: shopContext.shopMode === 'SINGLE_SUBDOMAIN' && !voiceSlug
            ? shopContext.speakerDisplayName || 'CrispyVoice'
            : await getMetaTitle(request),
        honeyProps,
        user,
        baseUrl: getUrlWithoutLang(request.url),
        env,
        gtmId: process.env.GTM_ID,
        plausibleDomain: process.env.PLAUSIBLE_DOMAIN_SPEAKER_SHOP,
        isEditModeValid: validateEditMode(request)
    }, { headers });
}

export default function App() {
    const data = useTypedLoaderData<typeof loader>();
    useChangeLanguage(data.locale);
    useFirstPartyGtm(data.gtmId);
    return (
        <EditModeProvider routeId='root'>
            <HoneypotProvider {...data.honeyProps}>
                <ThemeProvider theme={crispyvoiceTheme}>
                    <DefaultLangProvider value={defaultLang}>
                        <html
                            lang={data.locale ?? 'en'}
                            className='h-full scroll-smooth md:bg-slate-50 antialiased'
                            translate='no'
                        >
                            <head>
                                <meta charSet='utf-8' />
                                <meta name='viewport' content='width=device-width, initial-scale=1' />
                                <script
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            "try{var theme=localStorage.getItem('cv-theme');if(theme==='light'||theme==='dark')document.documentElement.setAttribute('data-core-theme',theme)}catch(error){}"
                                    }}
                                />
                                <style
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            "html[data-core-theme='light'] .core-unified,html[data-core-theme='light'] .core-booking-theme{--core-bg:#f5f8fa;--core-text:#0d2233;--core-text-rgb:13 34 51;--core-blue:#146f99;--core-blue-rgb:20 111 153;--core-accent:#b98908;--core-card-bg:#fff;--core-card-border:rgb(20 111 153 / .38);--core-input-bg:#eaf3f9;--core-input-border:rgb(20 111 153 / .35);--core-soft-bg:rgb(13 34 51 / .07);--core-soft-border:rgb(13 34 51 / .08);--core-muted-border:rgb(13 34 51 / .14)}"
                                    }}
                                />
                                <SessionId />
                                <ConsentHandling />
                                <PlausibleTracker domain={data.plausibleDomain} />
                                <Meta />
                                <Links />
                                <PublicEnv {...data.env} />
                            </head>
                            <body className='h-full w-full scroll-smooth'>
                                <AffiliateRefProvider>
                                    <main className='h-full w-full relative z-0'>
                                        <Outlet />
                                    </main>
                                </AffiliateRefProvider>
                                <ScrollRestoration />
                                <Scripts />
                                <MediumsIconLoader />
                            </body>
                        </html>
                    </DefaultLangProvider>
                </ThemeProvider>
            </HoneypotProvider>
        </EditModeProvider>
    );
}

export const links: LinksFunction = () => {
    return [ { rel: 'apple-touch-icon', sizes: '180x180', href: '/favicons/apple-touch-icon.png' }, {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicons/favicon-32x32.png'
    }, { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicons/favicon-16x16.png' } ];
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    return [ { title: data?.title ?? 'Error' } ];
};

export function ErrorBoundary() {
    return (
        <html lang='en' className='h-full scroll-smooth antialiased'>
            <head>
                <meta charSet='utf-8' />
                <meta name='viewport' content='width=device-width, initial-scale=1' />
                <Meta />
                <Links />
            </head>
            <body className='h-full w-full'>
                <GenericErrorBoundary homePath='/' />
                <Scripts />
            </body>
        </html>
    );
}
